import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceAPI, settingsAPI, llmConfigAPI, companyAPI } from '../api';
import { LLMSettingsModal } from '../components/WorkspaceModals';
import { FullscreenOutputModal } from '../components/FullscreenModals';
import ShareWorkspaceModal from '../components/ShareWorkspaceModal';
import SourcesPanel from '../components/SourcesPanel';
import ChatPanel from '../components/ChatPanel';
import ToolsPanel from '../components/ToolsPanel';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, Settings2, ChevronDown, ChevronLeft, ChevronRight,
  Settings, Loader2, Languages, Share2, Users, Pencil, Check, X,
  MessageSquareText, Zap, Tag, Info, Search, Trash2
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default function WorkspacePage() {
  const { id: workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [workspace, setWorkspace] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [webSources, setWebSources] = useState([]);
  const [textSources, setTextSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [llmSettings, setLlmSettings] = useState(null);
  const [llmOptions, setLlmOptions] = useState([]);
  const [wsLanguage, setWsLanguage] = useState('English');
  const [langOpen, setLangOpen] = useState(false);

  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [chatFullscreen, setChatFullscreen] = useState(false);
  const [llmOpen, setLlmOpen] = useState(false);
  const [llmSettingsOpen, setLlmSettingsOpen] = useState(false);
  const [fullscreenModal, setFullscreenModal] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [accessRole, setAccessRole] = useState('owner');
  const [shareCount, setShareCount] = useState(0);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [sysPromptOpen, setSysPromptOpen] = useState(false);
  const [llmConfigs, setLlmConfigs] = useState(null);

  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [mindMap, setMindMap] = useState(null);
  const [generatingMap, setGeneratingMap] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [wsTags, setWsTags] = useState([]);

  // Hydrate `summary` and `mindMap` from the most-recent successful AgentRun for this
  // workspace on first mount.  Without this, clicking "Summary" or "Mind Map" in the
  // Tools panel would re-run the LLM even though a perfectly good cached result already
  // lives in `BKW.AgentRuns` — wasting tokens and slowing the user down.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { toolsAPI } = await import('../api');
        const r = await toolsAPI.cachedResults(workspaceId);
        if (cancelled || !Array.isArray(r.data)) return;
        // cachedResults is sorted DESC by started_at — pick the first completed of each type.
        const latestSummary = r.data.find(x => x.type === 'tool:summarize' && x.status === 'completed' && x.result);
        const latestMindMap = r.data.find(x => x.type === 'tool:mindmap' && x.status === 'completed' && x.result);
        if (latestSummary?.result?.summary && !summary) {
          setSummary(latestSummary.result);
        }
        if (latestMindMap?.result?.branches && !mindMap) {
          setMindMap(latestMindMap.result);
        }
      } catch (e) { console.error('cached-results hydrate failed:', e); }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  // Summary / Mind Map handlers — extracted from the JSX so:
  //   1. Both ToolsPanel placements share one stable function (no inline lambda dup).
  //   2. We can short-circuit when a cached result already exists: clicking the tool
  //      then opens the FullscreenOutputModal instead of regenerating.
  const openOrGenerateSummary = async () => {
    if (summary) {
      // Cached → open the existing summary instead of burning another LLM call.
      setFullscreenModal({ type: 'summary', data: summary });
      return;
    }
    setSummarizing(true);
    try {
      const { insightAPI } = await import('../api');
      const res = await insightAPI.summarize(workspaceId, { document_ids: null });
      setSummary(res.data);
      setFullscreenModal({ type: 'summary', data: res.data });
    } catch (e) { console.error(e); }
    finally { setSummarizing(false); }
  };

  const openOrGenerateMindMap = async () => {
    if (mindMap && !mindMap._failed) {
      setFullscreenModal({ type: 'mindmap', data: mindMap, workspaceId });
      return;
    }
    setGeneratingMap(true);
    try {
      const { insightAPI } = await import('../api');
      const res = await insightAPI.mindmap(workspaceId, { document_ids: null });
      if (res.data?.status !== 'failed' && Array.isArray(res.data?.branches) && res.data.branches.length > 0) {
        setMindMap(res.data);
        setFullscreenModal({ type: 'mindmap', data: res.data, workspaceId });
      } else {
        setMindMap({ ...res.data, _failed: true });
      }
    } catch (e) {
      console.error(e);
      setMindMap({ _failed: true, error: e.response?.data?.detail || e.message || 'Mind map failed' });
    }
    finally { setGeneratingMap(false); }
  };

  // Monotonic request-version guard — prevents a slow bootstrap GET from overwriting a
  // newer reloadWorkspace() result (e.g. after a quick upload to a new workspace, the
  // initial GET resolves LAST and writes back its empty `documents` array, making the
  // freshly-uploaded source vanish until the next reload).  Every fetcher claims a
  // version up-front and only writes state if its version is still the latest.
  const loadVersionRef = useRef(0);

  // Workspace bootstrap effect — runs only on workspaceId change.
  // Setter functions from useState are guaranteed stable by React and intentionally
  // omitted from the dep array; including them would force needless re-runs without
  // any behaviour change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const myVersion = ++loadVersionRef.current;
    const load = async () => {
      setLoading(true);
      // Load from cache first for instant render
      try {
        const cached = sessionStorage.getItem(`bkw_ws_${workspaceId}`);
        if (cached) {
          const data = JSON.parse(cached);
          // Cached state is older than any in-flight reload; only apply if still latest.
          if (myVersion === loadVersionRef.current) {
            setWorkspace(data);
            setDocuments(data.documents || []);
            setWebSources(data.web_sources || []);
            setTextSources(data.text_sources || []);
            setLlmSettings(data.llm_settings);
            setLlmOptions(data.llm_options || []);
            setWsLanguage(data.language || 'English');
            setSystemPrompt(data.system_prompt || '');
            setAccessRole(data.access_role || 'owner');
            setShareCount(data.share_count || 0);
            document.title = `${data.name} | BrainShift AI Workspace`;
            setLoading(false);
          }
        }
      } catch (e) { console.error('Cache parse error:', e); }
      try {
        const res = await workspaceAPI.get(workspaceId);
        // If a later reload (e.g. post-upload) has already written state, do not clobber it.
        if (myVersion !== loadVersionRef.current) return;
        setWorkspace(res.data);
        setDocuments(res.data.documents || []);
        setWebSources(res.data.web_sources || []);
        setTextSources(res.data.text_sources || []);
        setLlmSettings(res.data.llm_settings);
        setLlmOptions(res.data.llm_options || []);
        setWsLanguage(res.data.language || 'English');
        setSystemPrompt(res.data.system_prompt || '');
        setAccessRole(res.data.access_role || 'owner');
        setShareCount(res.data.share_count || 0);
        document.title = `${res.data.name} | BrainShift AI Workspace`;
        setLoading(false);
        try { sessionStorage.setItem(`bkw_ws_${workspaceId}`, JSON.stringify(res.data)); } catch (e) { console.error('Cache write error:', e); }
        try {
          const cfgRes = await llmConfigAPI.list();
          if (myVersion === loadVersionRef.current) setLlmConfigs(cfgRes.data || []);
        } catch (e) { if (myVersion === loadVersionRef.current) setLlmConfigs([]); console.error('LLM config load error:', e); }
      } catch (err) {
        console.error(err);
        if (myVersion === loadVersionRef.current) setLoading(false);
      }
    };
    load();
    companyAPI.myPermissions().then(r => setPermissions(r.data?.permissions || {})).catch(() => {});
    workspaceAPI.getTags(workspaceId).then(r => setWsTags(r.data || [])).catch(() => {});
    // Note: no cleanup needed — loadVersionRef increment naturally invalidates the
    // previous in-flight request when this effect re-runs (workspaceId change).
  }, [workspaceId]);

  const reloadWorkspace = async () => {
    // Bump the version so any earlier bootstrap GET still in flight cannot overwrite us.
    const myVersion = ++loadVersionRef.current;
    try {
      const res = await workspaceAPI.get(workspaceId);
      if (myVersion !== loadVersionRef.current) return;
      setWorkspace(res.data);
      setDocuments(res.data.documents || []);
      setWebSources(res.data.web_sources || []);
      setTextSources(res.data.text_sources || []);
      try { sessionStorage.setItem(`bkw_ws_${workspaceId}`, JSON.stringify(res.data)); } catch (e) { console.error('Cache write error:', e); }
    } catch (e) { console.error(e); }
  };

  // Phase 1: optimistic update + cancellation. The UI applies the change instantly,
  // network call happens in the background, error reverts to previous selection.
  // Prevents the "stuck/slow" feel users reported on rapid model switches.
  const changeLLMRequest = useRef(0);
  const changeLLM = (opt) => {
    const reqId = ++changeLLMRequest.current;
    const prev = llmSettings;
    setLlmSettings({ provider: opt.provider, model: opt.model });
    setLlmOpen(false);
    settingsAPI.updateLLM(workspaceId, { provider: opt.provider, model: opt.model })
      .catch((e) => {
        console.error('LLM switch failed:', e);
        // Only revert if this request is still the latest in-flight attempt.
        if (reqId === changeLLMRequest.current) setLlmSettings(prev);
      });
  };

  const changeLanguage = async (lang) => {
    setWsLanguage(lang);
    setLangOpen(false);
    try {
      await settingsAPI.updateLanguage(workspaceId, lang);
    } catch (e) {
      console.error(e);
      setWsLanguage(wsLanguage); // revert on error
    }
  };

  const renameWorkspace = async (newName) => {
    if (!newName.trim() || newName === workspace?.name) return;
    try {
      await workspaceAPI.update(workspaceId, { name: newName });
      setWorkspace(prev => ({ ...prev, name: newName }));
      document.title = `${newName} | BrainShift AI Workspace`;
    } catch (e) { console.error(e); }
  };

  const saveSystemPrompt = async (prompt) => {
    setSystemPrompt(prompt);
    try {
      await settingsAPI.updateSystemPrompt(workspaceId, prompt);
    } catch (e) { console.error(e); }
  };

  const totalSources = documents.length + webSources.length + textSources.length;
  const currentLLM = llmOptions.find(o => o.model === llmSettings?.model) || llmOptions[0];

  // Mobile detection: auto-hide panels.
  // Effect deliberately empty-dep — viewport handler attaches once on mount; setIsMobile,
  // setLeftOpen, setRightOpen are stable React setters.
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) { setLeftOpen(false); setRightOpen(false); }
    };
    window.addEventListener('resize', handler);
    // On initial mobile load, hide panels
    if (window.innerWidth < 768) { setLeftOpen(false); setRightOpen(false); }
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (loading) return <LoadingScreen />;

  return (
    <div className="h-[100dvh] flex flex-col bg-[#F8F9FA]" data-testid="workspace-page">
      <Header
        workspace={workspace} currentLLM={currentLLM} llmOpen={llmOpen} setLlmOpen={setLlmOpen}
        llmOptions={llmOptions} llmSettings={llmSettings} onChangeLLM={changeLLM}
        leftOpen={leftOpen} setLeftOpen={setLeftOpen} rightOpen={rightOpen} setRightOpen={setRightOpen}
        onLlmSettings={() => setLlmSettingsOpen(true)} onBack={() => navigate('/dashboard')}
        wsLanguage={wsLanguage} langOpen={langOpen} setLangOpen={setLangOpen} onChangeLanguage={changeLanguage}
        onShare={() => setShareOpen(true)} shareCount={shareCount} accessRole={accessRole}
        onRename={renameWorkspace}
        onDelete={async () => {
          if (!window.confirm(`Permanently delete workspace "${workspace?.name}"? All sources, notes and chat history will be removed. This cannot be undone.`)) return;
          try {
            await workspaceAPI.delete(workspaceId);
            navigate('/dashboard');
          } catch (e) {
            alert(e.response?.data?.detail || 'Delete failed');
          }
        }}
        systemPrompt={systemPrompt} sysPromptOpen={sysPromptOpen} setSysPromptOpen={setSysPromptOpen}
        onSaveSystemPrompt={saveSystemPrompt}
        isMobile={isMobile}
        permissions={permissions}
        isSuperAdmin={!!user?.is_super_admin}
        wsTags={wsTags}
        onAddTag={async (tag) => { await workspaceAPI.addTag(workspaceId, tag); const r = await workspaceAPI.getTags(workspaceId); setWsTags(r.data || []); }}
        onRemoveTag={async (tag) => { await workspaceAPI.removeTag(workspaceId, tag); const r = await workspaceAPI.getTags(workspaceId); setWsTags(r.data || []); }}
      />
      <div className="flex-1 flex overflow-hidden relative">
        {/* Sources panel - mobile: absolute overlay */}
        {isMobile ? (
          leftOpen && !chatFullscreen && (
            <div className="absolute inset-0 z-30 flex" data-testid="mobile-sources-overlay">
              <div className="w-[85vw] max-w-[360px] bg-white border-r border-[#E5E7EB] shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">
                <SourcesPanel isVisible={true} workspaceId={workspaceId} documents={documents} webSources={webSources} textSources={textSources} onReload={reloadWorkspace} onClosePanel={() => setLeftOpen(false)} />
              </div>
              <div className="flex-1 bg-black/30" onClick={() => setLeftOpen(false)} />
            </div>
          )
        ) : (
          <SourcesPanel isVisible={leftOpen && !chatFullscreen} workspaceId={workspaceId} documents={documents} webSources={webSources} textSources={textSources} onReload={reloadWorkspace} onClosePanel={() => setLeftOpen(false)} />
        )}

        {/* Chat panel - always visible */}
        <ChatPanel workspaceId={workspaceId} totalSources={totalSources} chatFullscreen={chatFullscreen} setChatFullscreen={setChatFullscreen} />

        {/* Edge re-open tabs — shown only when the corresponding panel is collapsed
            (desktop). Notion/NotebookLM-style vertical tabs anchored to the chat edges
            with a visible label so users immediately discover them. */}
        {!isMobile && !leftOpen && !chatFullscreen && (
          <button
            onClick={() => setLeftOpen(true)}
            className="group absolute left-0 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center justify-center gap-1.5 h-32 w-7 bg-white border border-l-0 border-[#E5E7EB] rounded-r-xl shadow-md hover:w-9 hover:shadow-lg hover:bg-[#F8FAFF] hover:border-[#6590FF]/50 hover:text-[#6590FF] transition-all duration-200 text-[#6B7280]"
            data-testid="reopen-sources-panel"
            title="Show sources panel"
            aria-label="Show sources panel"
          >
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
            >
              Sources
            </span>
          </button>
        )}
        {!isMobile && !rightOpen && !chatFullscreen && (
          <button
            onClick={() => setRightOpen(true)}
            className="group absolute right-0 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center justify-center gap-1.5 h-32 w-7 bg-white border border-r-0 border-[#E5E7EB] rounded-l-xl shadow-md hover:w-9 hover:shadow-lg hover:bg-[#F8FAFF] hover:border-[#6590FF]/50 hover:text-[#6590FF] transition-all duration-200 text-[#6B7280]"
            data-testid="reopen-tools-panel"
            title="Show tools panel"
            aria-label="Show tools panel"
          >
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
            <span
              className="text-[10px] font-bold uppercase tracking-wider"
              style={{ writingMode: 'vertical-rl' }}
            >
              Tools
            </span>
          </button>
        )}

        {/* Tools panel - mobile: absolute overlay */}
        {isMobile ? (
          rightOpen && !chatFullscreen && (
            <div className="absolute inset-0 z-30 flex justify-end" data-testid="mobile-tools-overlay">
              <div className="flex-1 bg-black/30" onClick={() => setRightOpen(false)} />
              <div className="w-[85vw] max-w-[416px] bg-white border-l border-[#E5E7EB] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
                <ToolsPanel workspaceId={workspaceId} totalSources={totalSources} onOpenFullscreen={setFullscreenModal}
                  workspaceLanguage={wsLanguage}
                  existingSummary={summary} existingMindMap={mindMap}
                  onGenSummary={openOrGenerateSummary}
                  onGenMindMap={openOrGenerateMindMap}
                  summarizing={summarizing} generatingMap={generatingMap}
                  onClosePanel={() => setRightOpen(false)} />
              </div>
            </div>
          )
        ) : (
          <div className={`bg-white flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out ${rightOpen && !chatFullscreen ? 'w-[416px] min-w-[416px] max-w-[416px] border-l border-[#E5E7EB]' : 'w-0 min-w-0 max-w-0 border-l-0 overflow-hidden'}`} data-testid="tools-panel">
            <ToolsPanel workspaceId={workspaceId} totalSources={totalSources} onOpenFullscreen={setFullscreenModal}
              workspaceLanguage={wsLanguage}
              existingSummary={summary} existingMindMap={mindMap}
              onGenSummary={openOrGenerateSummary}
              onGenMindMap={openOrGenerateMindMap}
              summarizing={summarizing} generatingMap={generatingMap}
              onClosePanel={() => setRightOpen(false)} />
          </div>
        )}
      </div>
      {fullscreenModal && <FullscreenOutputModal data={{ ...fullscreenModal, workspaceId }} onClose={() => setFullscreenModal(null)} />}
      {llmSettingsOpen && <LLMSettingsModal workspaceId={workspaceId} onClose={() => setLlmSettingsOpen(false)} initialConfigs={llmConfigs} onConfigsChange={setLlmConfigs} />}
      {shareOpen && <ShareWorkspaceModal workspaceId={workspaceId} workspaceName={workspace?.name} onClose={() => setShareOpen(false)} />}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="h-[100dvh] flex items-center justify-center bg-[#F8F9FA]" data-testid="workspace-loader">
      <div className="flex flex-col items-center gap-6">
        {/* Animated rings */}
        <div className="relative w-20 h-20">
          <div className="absolute inset-0 rounded-full border-2 border-[#6590FF]/10 animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute inset-2 rounded-full border-2 border-[#6590FF]/20 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.2s' }} />
          <div className="absolute inset-4 rounded-full border-2 border-[#6590FF]/30 animate-ping" style={{ animationDuration: '1s', animationDelay: '0.4s' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-8 h-8 rounded-xl bg-[#6590FF] flex items-center justify-center animate-pulse">
              <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>
            </div>
          </div>
        </div>
        {/* Loading text with typing effect */}
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-base font-semibold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Initializing Workspace</span>
            <span className="flex gap-0.5">
              <span className="w-1 h-1 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-1 h-1 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-1 h-1 rounded-full bg-[#6590FF] animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          </div>
          <p className="text-sm text-[#9CA3AF]">Loading documents, models, and tools</p>
        </div>
        {/* Progress bar */}
        <div className="w-48 h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
          <div className="h-full bg-[#6590FF] rounded-full" style={{ animation: 'loading-forward 8s cubic-bezier(0.4, 0, 0.2, 1) forwards' }} />
        </div>
        <style>{`@keyframes loading-forward { 0% { width: 0%; } 20% { width: 30%; } 50% { width: 60%; } 80% { width: 85%; } 100% { width: 95%; } }`}</style>
      </div>
    </div>
  );
}

function Header({ workspace, currentLLM, llmOpen, setLlmOpen, llmOptions, llmSettings, onChangeLLM, leftOpen, setLeftOpen, rightOpen, setRightOpen, onLlmSettings, onBack, wsLanguage, langOpen, setLangOpen, onChangeLanguage, onShare, shareCount, accessRole, onRename, onDelete, systemPrompt, sysPromptOpen, setSysPromptOpen, onSaveSystemPrompt, isMobile, permissions, isSuperAdmin, wsTags, onAddTag, onRemoveTag }) {
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState('');
  const [tagInput, setTagInput] = React.useState('');
  const [showTagInput, setShowTagInput] = React.useState(false);
  const inputRef = React.useRef(null);

  const startEditing = () => {
    setEditName(workspace?.name || '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const confirmRename = () => {
    if (editName.trim() && editName !== workspace?.name) {
      onRename(editName.trim());
    }
    setEditing(false);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditName('');
  };

  return (
    <>
    <header className="h-12 border-b border-[#E5E7EB] bg-white flex items-center px-3 sm:px-4 gap-2 sm:gap-3 flex-shrink-0 z-30">
      <button onClick={onBack} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors flex-shrink-0" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4 text-[#4B5563]" />
      </button>
      {/* Workspace-only branding: use the "internal" mark (icon-only) here to give the
          workspace shell a more focused, application-like feel.  The full wordmark
          continues to live on the public marketing surfaces (login, dashboard, register). */}
      <img src="/brainshift-logo-internal.png" alt="BrainShift" className="w-auto hidden sm:block" style={{ height: '1.5rem' }} data-testid="workspace-internal-logo" />
      {editing ? (
        <div className="flex items-center gap-1.5">
          <input ref={inputRef} type="text" value={editName} onChange={e => setEditName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') cancelEditing(); }}
            onBlur={confirmRename}
            className="text-base font-bold text-[#111827] border border-[#6590FF] rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#6590FF]/20 w-40 sm:w-56"
            data-testid="workspace-name-edit-input"
            style={{ fontFamily: 'var(--font-heading)' }} />
        </div>
      ) : (
        <div className="flex items-center gap-1.5 group min-w-0">
          <span className="text-base font-bold text-[#111827] truncate max-w-[120px] sm:max-w-none" style={{ fontFamily: 'var(--font-heading)' }} title={workspace?.name}>{workspace?.name}</span>
          <button onClick={startEditing} className="p-1 hover:bg-[#F3F4F6] rounded-lg opacity-0 group-hover:opacity-100 transition-all flex-shrink-0" data-testid="rename-workspace-btn" title="Rename workspace">
            <Pencil className="w-3 h-3 text-[#9CA3AF]" />
          </button>
        </div>
      )}
      {accessRole !== 'owner' && (
        <span className="text-[11px] px-2 py-0.5 rounded-lg bg-[#F3F4F6] text-[#6B7280] font-medium capitalize">{accessRole}</span>
      )}
      {/* Tags */}
      <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
        {(wsTags || []).map(tag => (
          <span key={tag} className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-[#6590FF] bg-[#6590FF]/5 px-2 py-0.5 border border-[#6590FF]/20 rounded-full">
            {tag}
            <button onClick={() => onRemoveTag(tag)} className="hover:text-[#C8102E] ml-0.5" data-testid={`ws-remove-tag-${tag}`}>
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        {showTagInput ? (
          <div className="flex items-center gap-1">
            <input value={tagInput} onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && tagInput.trim()) { onAddTag(tagInput.trim()); setTagInput(''); setShowTagInput(false); } if (e.key === 'Escape') setShowTagInput(false); }}
              placeholder="Tag..."
              className="h-6 w-20 px-1.5 text-[11px] border border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#6590FF]"
              autoFocus data-testid="ws-header-tag-input" />
            <button onClick={() => setShowTagInput(false)} className="text-[#9CA3AF] hover:text-[#111827]"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button onClick={() => setShowTagInput(true)}
            className="text-[11px] font-medium text-[#9CA3AF] hover:text-[#6590FF] px-1.5 py-0.5 border border-dashed border-[#D1D5DB] hover:border-[#6590FF] rounded-full transition-colors flex items-center gap-0.5"
            data-testid="ws-header-add-tag-btn">
            <Tag className="w-2.5 h-2.5" /> tag
          </button>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <button onClick={() => setSysPromptOpen(true)}
          className={`hidden sm:flex h-7 px-2.5 border rounded-xl text-sm font-medium items-center gap-1.5 transition-colors ${systemPrompt ? 'border-[#6590FF]/30 bg-[#6590FF]/5 text-[#6590FF]' : 'border-[#E5E7EB] text-[#4B5563] hover:bg-[#F3F4F6]'}`}
          data-testid="system-prompt-btn" title="Workspace System Prompt">
          <MessageSquareText className="w-3.5 h-3.5" /> System Prompt
        </button>
        <div className="hidden sm:block"><LanguageSelector wsLanguage={wsLanguage} langOpen={langOpen} setLangOpen={setLangOpen} onChangeLanguage={onChangeLanguage} /></div>
        {(isSuperAdmin || permissions.can_change_ai_model) && (
          <div className="hidden sm:block"><LLMSelector currentLLM={currentLLM} llmOpen={llmOpen} setLlmOpen={setLlmOpen} llmOptions={llmOptions} llmSettings={llmSettings} onChangeLLM={onChangeLLM} /></div>
        )}
        {permissions.can_use_advanced !== false && (
          <button onClick={() => window.location.href = `/workspace/${workspace?.id}/advanced`}
            className="h-7 px-2.5 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#6590FF]/5 hover:text-[#6590FF] hover:border-[#6590FF]/30 flex items-center gap-1.5 transition-colors"
            data-testid="advanced-tools-btn" title="Advanced Tools">
            <Zap className="w-3 h-3" /> <span className="hidden lg:inline">Advanced</span>
          </button>
        )}
        <button onClick={onShare} className="h-7 px-2.5 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#F3F4F6] flex items-center gap-1.5 transition-colors" data-testid="share-workspace-btn">
          <Share2 className="w-3 h-3" /> Share
          {shareCount > 0 && <span className="w-4 h-4 flex items-center justify-center rounded-full bg-[#6590FF] text-white text-[10px] font-bold">{shareCount}</span>}
        </button>
        {/* Panel toggle buttons removed — each panel now has its own close button (PanelLeftClose
            inside Sources panel header, PanelRightClose inside Tools panel header). */}
        {(isSuperAdmin || (permissions.can_view_settings !== false && permissions.can_change_ai_model)) && (
          <button onClick={onLlmSettings} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors" data-testid="llm-settings-btn" title="LLM Settings"><Settings className="w-4 h-4 text-[#4B5563]" /></button>
        )}
        {/* Delete-workspace action moved to the per-card menu on the dashboard so users
            don't accidentally delete from inside an open workspace. */}
      </div>
    </header>

    {/* System Prompt Popup */}
    {sysPromptOpen && (
      <SystemPromptPopup
        prompt={systemPrompt}
        onSave={(p) => { onSaveSystemPrompt(p); setSysPromptOpen(false); }}
        onClose={() => setSysPromptOpen(false)}
      />
    )}
    </>
  );
}

function SystemPromptPopup({ prompt, onSave, onClose }) {
  const [draft, setDraft] = React.useState(prompt || '');
  const textareaRef = React.useRef(null);
  React.useEffect(() => { setTimeout(() => textareaRef.current?.focus(), 100); }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="system-prompt-overlay">
      <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="system-prompt-dialog">
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-[#6590FF]" />
            <span className="text-base font-bold text-[#111827]" style={{ fontFamily: 'var(--font-heading)' }}>Workspace System Prompt</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-xl transition-colors"><X className="w-4 h-4 text-[#4B5563]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-[#6B7280]">
            This prompt applies to all AI interactions in this workspace — chat, tools, and agents. Use it to set the AI's role, tone, constraints, or domain focus.
          </p>
          <Textarea
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={8}
            className="rounded-xl resize-none text-base"
            placeholder="e.g., You are a legal compliance expert specializing in GDPR and data protection regulations. Always cite specific articles and clauses when answering questions."
            data-testid="system-prompt-textarea"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#9CA3AF]">{draft.length} characters</span>
            <div className="flex items-center gap-2">
              {draft && <button onClick={() => setDraft('')} className="h-8 px-3 text-sm font-medium text-[#DC2626] hover:bg-[#FEE2E2] rounded-xl transition-colors" data-testid="clear-prompt-btn">Clear</button>}
              <button onClick={onClose} className="h-8 px-3 text-sm font-medium text-[#4B5563] hover:bg-[#F3F4F6] rounded-xl transition-colors">Cancel</button>
              <button onClick={() => onSave(draft)} className="h-8 px-4 bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-sm font-semibold rounded-xl transition-colors" data-testid="save-prompt-btn">Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LLMSelector({ currentLLM, llmOpen, setLlmOpen, llmOptions, llmSettings, onChangeLLM }) {
  return (
    <div className="relative">
      <button onClick={() => setLlmOpen(!llmOpen)} className="h-7 px-3 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#F3F4F6] flex items-center gap-1.5 transition-colors" data-testid="llm-selector">
        <Settings2 className="w-3 h-3" />
        {currentLLM?.label || 'Select LLM'}
        <ChevronDown className="w-3 h-3" />
      </button>
      {llmOpen && (
        <div className="absolute right-0 top-8 w-52 bg-white border border-[#E5E7EB] rounded-xl shadow-lg z-50 overflow-hidden">
          {llmOptions.map(opt => (
            <button key={opt.model} onClick={() => onChangeLLM(opt)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F3F4F6] transition-colors ${opt.model === llmSettings?.model ? 'bg-[#6590FF]/5 text-[#6590FF] font-semibold' : 'text-[#4B5563]'}`}
              data-testid={`llm-option-${opt.provider}`}>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


const LANGUAGES = [
  'Afrikaans', 'Albanian', 'Amharic', 'Arabic', 'Armenian', 'Azerbaijani',
  'Basque', 'Belarusian', 'Bengali', 'Bosnian', 'Bulgarian', 'Burmese',
  'Catalan', 'Cebuano', 'Chichewa', 'Chinese (Simplified)', 'Chinese (Traditional)',
  'Corsican', 'Croatian', 'Czech', 'Danish', 'Dutch', 'English', 'Esperanto',
  'Estonian', 'Filipino', 'Finnish', 'French', 'Frisian', 'Galician',
  'Georgian', 'German', 'Greek', 'Gujarati', 'Haitian Creole', 'Hausa',
  'Hawaiian', 'Hebrew', 'Hindi', 'Hmong', 'Hungarian', 'Icelandic', 'Igbo',
  'Indonesian', 'Irish', 'Italian', 'Japanese', 'Javanese', 'Kannada',
  'Kazakh', 'Khmer', 'Kinyarwanda', 'Korean', 'Kurdish', 'Kyrgyz', 'Lao',
  'Latin', 'Latvian', 'Lithuanian', 'Luxembourgish', 'Macedonian', 'Malagasy',
  'Malay', 'Malayalam', 'Maltese', 'Maori', 'Marathi', 'Mongolian', 'Nepali',
  'Norwegian', 'Odia', 'Pashto', 'Persian', 'Polish', 'Portuguese', 'Punjabi',
  'Romanian', 'Russian', 'Samoan', 'Scots Gaelic', 'Serbian', 'Sesotho',
  'Shona', 'Sindhi', 'Sinhala', 'Slovak', 'Slovenian', 'Somali', 'Spanish',
  'Sundanese', 'Swahili', 'Swedish', 'Tajik', 'Tamil', 'Tatar', 'Telugu',
  'Thai', 'Turkish', 'Turkmen', 'Ukrainian', 'Urdu', 'Uyghur', 'Uzbek',
  'Vietnamese', 'Welsh', 'Xhosa', 'Yiddish', 'Yoruba', 'Zulu'
];

function LanguageSelector({ wsLanguage, langOpen, setLangOpen, onChangeLanguage }) {
  const [search, setSearch] = useState('');
  const filtered = search.trim()
    ? LANGUAGES.filter(l => l.toLowerCase().includes(search.toLowerCase()))
    : LANGUAGES;

  return (
    <div className="relative flex items-center gap-1">
      <button onClick={() => { setLangOpen(!langOpen); setSearch(''); }}
        className="h-7 px-2.5 border border-[#E5E7EB] rounded-xl text-sm font-medium text-[#4B5563] hover:bg-[#F3F4F6] flex items-center gap-1.5 transition-colors"
        data-testid="language-selector">
        <Languages className="w-3 h-3" />
        {wsLanguage}
        <ChevronDown className="w-3 h-3" />
      </button>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button"
              className="p-1 rounded-full hover:bg-[#F3F4F6] text-[#9CA3AF] hover:text-[#6590FF] transition-colors"
              data-testid="language-info-icon"
              aria-label="Language help">
              <Info className="w-3.5 h-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-sm max-w-[220px]">
            Select the AI default output language
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {langOpen && (
        <div className="absolute right-0 top-9 w-56 bg-white border border-[#E5E7EB] rounded-xl shadow-xl z-50 flex flex-col" data-testid="language-dropdown">
          <div className="p-2 border-b border-[#E5E7EB] flex items-center gap-2 sticky top-0 bg-white rounded-t-xl">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />
            <input
              type="text"
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search language…"
              className="flex-1 text-sm bg-transparent outline-none placeholder:text-[#9CA3AF]"
              data-testid="language-search-input"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-[#9CA3AF] px-3 py-3 text-center">No languages found</p>
            ) : (
              filtered.map(lang => (
                <button key={lang} onClick={() => { onChangeLanguage(lang); setSearch(''); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F3F4F6] transition-colors ${lang === wsLanguage ? 'bg-[#6590FF]/5 text-[#6590FF] font-semibold' : 'text-[#4B5563]'}`}
                  data-testid={`lang-option-${lang}`}>
                  {lang}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
